import { memo, type CSSProperties } from 'react';
import { temporalStateAt } from './treeYears';
import { useTreeStore } from '@/store/useTreeStore';
import { useAuthStore } from '@/store/useAuthStore';
import type { TreeNode } from '@/api/tree.api';
import type { OverlayStatus } from '@/api/medicalOverlay.api';
import { OVERLAY_STATUS_STYLE } from './overlayStatus';

// ─── Konstanten ─────────────────────────────────────────────────────────────

const NODE_W = 160;
const NODE_H = 90;
const HALF_W = NODE_W / 2;
const HALF_H = NODE_H / 2;
const GENDER_BAR_W = 4;
const CORNER_R = 8;

// Gender → Farbe für den linken Balken
const GENDER_COLORS: Record<string, string> = {
  male: '#93c5fd',   // blue-300
  female: '#f9a8d4', // pink-300
  other: '#86efac',  // green-300
  unknown: '#cbd5e1', // slate-300
};

// ─── SVG Drop-Shadow Filter (einmal in <defs> gerendert) ────────────────────

export function PersonNodeDefs() {
  return (
    <defs>
      <filter id="node-shadow" x="-10%" y="-10%" width="130%" height="140%">
        <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000" floodOpacity="0.08" />
      </filter>
      <filter id="node-shadow-hover" x="-10%" y="-10%" width="130%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.12" />
      </filter>
      <clipPath id="avatar-clip">
        <circle cx="0" cy="0" r="14" />
      </clipPath>
      {/* Schraffur für „unbekannt" – macht fehlende Daten visuell eindeutig (≠ „kein Risiko"). */}
      <pattern id="overlay-unknown-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="#f8fafc" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="#cbd5e1" strokeWidth="2" />
      </pattern>
    </defs>
  );
}

// ─── Hilffunktion: Lebensdaten formatieren ──────────────────────────────────

function formatBirthLine(birth: string | null): string {
  return birth ? `* ${birth}` : '';
}

function formatDeathLine(death: string | null): string {
  return death ? `† ${death}` : '';
}

// ─── Hilffunktion: Text kürzen ──────────────────────────────────────────────

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen - 1) + '…' : text;
}

// ─── PersonNode ─────────────────────────────────────────────────────────────

interface PersonNodeProps {
  x: number;
  y: number;
  data: TreeNode;
  /** Aktives Medizin-Overlay: abgeleiteter Status dieser Person (optional). */
  overlayStatus?: OverlayStatus;
  /** Trägerwahrscheinlichkeit (0..1) für Tooltip bei 'at_risk'. */
  overlayProbability?: number | null;
  /** Aktives Jahr des Zeit-Schiebereglers (null = aus). */
  timeYear?: number | null;
  /** Rechtsklick auf die Karte (öffnet das Kontextmenü). */
  onContextMenu?: (personId: string, event: React.MouseEvent) => void;
  /**
   * Erkundungsansicht: so viele direkte Verwandte dieser Person sind gerade
   * nicht zu sehen. Undefined oder 0 = kein Zähler.
   */
  verdeckt?: number;
  /** Klick auf den Zähler – holt die Verwandten dazu. */
  onAufdecken?: (personId: string) => void;
  /**
   * Echter Schlagschatten (SVG-Filter) statt eines nachgezeichneten.
   *
   * Ein `filter` zwingt den Browser für JEDE Karte zu einem eigenen
   * Zwischenbild. Bei 3000 Karten kostete allein das 43 ms je Bildaufbau –
   * ohne Filter waren es 6 ms. Die Leinwand schaltet ihn deshalb ab einer
   * gewissen Kartenzahl ab; an seine Stelle tritt ein leicht versetztes graues
   * Rechteck, das ohne eigenen Zeichendurchgang auskommt.
   */
  echterSchatten?: boolean;
}

export const PersonNode = memo(function PersonNode({
  x,
  y,
  data,
  overlayStatus,
  overlayProbability,
  timeYear,
  onContextMenu,
  verdeckt,
  onAufdecken,
  echterSchatten = true,
}: PersonNodeProps) {
  const selectedPersonId = useTreeStore((s) => s.selectedPersonId);
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const accessToken = useAuthStore((s) => s.accessToken);

  const isSelected = selectedPersonId === data.id;
  const overlay = overlayStatus ? OVERLAY_STATUS_STYLE[overlayStatus] : null;
  const overlayTitle = overlay
    ? `${overlay.label}${overlayProbability != null ? ` · P(Träger:in) ≈ ${Math.round(overlayProbability * 100)}%` : ''}`
    : null;
  const genderColor = GENDER_COLORS[data.gender ?? 'unknown'] ?? GENDER_COLORS.unknown;
  const fullName = `${data.firstName} ${data.lastName ?? ''}`.trim();

  // Zeit-Schieberegler: im gewählten Jahr Verstorbene ausgrauen, noch nicht
  // Geborene stark ausblenden. Ohne Datumsdaten bleibt der Knoten normal.
  const temporal = timeYear != null ? temporalStateAt(data.birthDateText, data.deathDateText, timeYear) : null;
  const timeStyle: CSSProperties =
    temporal === 'unborn'
      ? { opacity: 0.1 }
      : temporal === 'dead'
        ? { opacity: 0.45, filter: 'grayscale(1)' }
        : {};
  const birthLine = formatBirthLine(data.birthDateText);
  const deathLine = formatDeathLine(data.deathDateText);
  // Zugriff über das httpOnly-Medien-Cookie – kein Token in der URL.
  const avatarUrl = data.profileMediaFilename && accessToken
    ? `/uploads/${data.profileMediaFilename}`
    : null;

  // Tooltip immer anzeigen: Namen können gekürzt sein – ohne Tooltip wäre
  // der volle Name im Baum sonst nicht lesbar.
  const lifespan = [birthLine, deathLine].filter(Boolean).join('  ');
  const nodeTitle = `${fullName}${lifespan ? ` (${lifespan})` : ''}${overlayTitle ? ` – ${overlayTitle}` : ''}`;

  // Lange Namen zweizeilig (Vorname / Nachname) statt hart gekürzt –
  // wichtig v. a. auf Touch-Geräten, wo der Tooltip nicht erreichbar ist.
  const NAME_MAX = 16;
  const nameLines: string[] =
    fullName.length <= NAME_MAX || !data.lastName
      ? [truncate(fullName, NAME_MAX)]
      : [truncate(data.firstName, NAME_MAX), truncate(data.lastName, NAME_MAX)];
  const dateLines: string[] = [birthLine, deathLine].filter(Boolean);

  // Textblock (Name + Lebensdaten) vertikal in der Karte zentrieren.
  // Mit Overlay-Badge (oben rechts, reicht bis y=22) den Block nach unten
  // klemmen, damit die erste Namenszeile nicht unter das Badge läuft –
  // nach unten ist Platz, der Schraffur-Streifen beginnt erst bei y=84.
  const NAME_LH = 16;
  const DATE_LH = 15;
  const textBlockH = nameLines.length * NAME_LH + dateLines.length * DATE_LH;
  const centeredTop = HALF_H - textBlockH / 2;
  const textBlockTop = overlay ? Math.max(centeredTop, 21) : centeredTop;
  const dateBlockTop = textBlockTop + nameLines.length * NAME_LH;

  const toggleSelect = () => setSelectedPerson(isSelected ? null : data.id);

  return (
    <g
      transform={`translate(${x - HALF_W},${y - HALF_H})`}
      onClick={(e) => {
        e.stopPropagation();
        toggleSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          toggleSelect();
        }
      }}
      onContextMenu={(e) => {
        if (!onContextMenu) return;
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(data.id, e);
      }}
      role="button"
      tabIndex={0}
      aria-label={`${fullName} auswählen`}
      aria-pressed={isSelected}
      style={{ cursor: 'pointer', transition: 'opacity 0.15s', ...timeStyle }}
      className="person-node"
    >
      <title>{nodeTitle}</title>

      {/* Nachgezeichneter Schatten für große Bäume: ein Rechteck statt eines
          Filters. Sieht auf den ersten Blick gleich aus und kostet ein
          Element statt eines eigenen Zeichendurchgangs. */}
      {!echterSchatten && (
        <rect
          y={1.5}
          width={NODE_W}
          height={NODE_H}
          rx={CORNER_R}
          ry={CORNER_R}
          fill="#0f172a"
          opacity={0.1}
        />
      )}

      {/* Hintergrund-Rechteck */}
      <rect
        width={NODE_W}
        height={NODE_H}
        rx={CORNER_R}
        ry={CORNER_R}
        fill="#ffffff"
        stroke={isSelected ? '#3b82f6' : overlay ? overlay.ring : '#e2e8f0'}
        strokeWidth={isSelected ? 2 : overlay ? 2.5 : 1}
        filter={
          echterSchatten
            ? isSelected
              ? 'url(#node-shadow-hover)'
              : 'url(#node-shadow)'
            : undefined
        }
      />

      {/* „unbekannt": schraffierter Streifen unten – klar ≠ „kein Risiko" */}
      {overlay?.hatched && (
        <rect
          x={GENDER_BAR_W}
          y={NODE_H - 6}
          width={NODE_W - GENDER_BAR_W - CORNER_R}
          height={6}
          fill="url(#overlay-unknown-hatch)"
        />
      )}

      {/* Gender-Farbbalken links */}
      <rect
        x={0}
        y={0}
        width={GENDER_BAR_W}
        height={NODE_H}
        rx={CORNER_R}
        fill={genderColor}
        clipPath={`inset(0 0 0 0 round ${CORNER_R}px 0 0 ${CORNER_R}px)`}
      />
      {/* Sauberer Balken: nur linke Ecken runden */}
      <rect
        x={0}
        y={0}
        width={GENDER_BAR_W}
        height={NODE_H}
        fill={genderColor}
      />
      <rect
        x={0}
        y={0}
        width={GENDER_BAR_W + 1}
        height={CORNER_R}
        fill={genderColor}
        rx={0}
      />
      {/* Obere linke Ecke abrunden */}
      <path
        d={`M0,${CORNER_R} Q0,0 ${CORNER_R},0 L${GENDER_BAR_W},0 L${GENDER_BAR_W},${CORNER_R} Z`}
        fill={genderColor}
      />
      {/* Untere linke Ecke abrunden */}
      <path
        d={`M0,${NODE_H - CORNER_R} Q0,${NODE_H} ${CORNER_R},${NODE_H} L${GENDER_BAR_W},${NODE_H} L${GENDER_BAR_W},${NODE_H - CORNER_R} Z`}
        fill={genderColor}
      />

      {/* Profilfoto oder Platzhalter (Kreis) */}
      <circle
        cx={24}
        cy={NODE_H / 2}
        r={14}
        fill={data.profileMediaFilename ? '#e2e8f0' : '#f1f5f9'}
        stroke="#cbd5e1"
        strokeWidth={1}
      />
      {/* Profilbild anzeigen wenn vorhanden */}
      {data.profileMediaFilename && (
        <g transform={`translate(24,${NODE_H / 2})`}>
          <clipPath id={`avatar-clip-${data.id}`}>
            <circle cx={0} cy={0} r={13.5} />
          </clipPath>
          <image
            href={avatarUrl ?? undefined}
            x={-14}
            y={-14}
            width={28}
            height={28}
            clipPath={`url(#avatar-clip-${data.id})`}
            preserveAspectRatio="xMidYMid slice"
          />
        </g>
      )}
      {/* Profil-Icon (Silhouette) wenn kein Foto */}
      {!data.profileMediaFilename && (
        <g transform={`translate(24,${NODE_H / 2})`}>
          <circle cx={0} cy={-4} r={4.5} fill="#94a3b8" />
          <path
            d="M-7,8 Q-7,2 0,2 Q7,2 7,8"
            fill="#94a3b8"
          />
        </g>
      )}

      {/* Name (fett, bei langen Namen zweizeilig: Vorname / Nachname) */}
      {nameLines.map((line, i) => (
        <text
          key={`name-${i}`}
          x={46}
          y={textBlockTop + i * NAME_LH + NAME_LH / 2}
          fontSize={13}
          fontWeight={600}
          fill="#1e293b"
          dominantBaseline="middle"
        >
          {line}
        </text>
      ))}

      {/* Lebensdaten (Geburt/Tod) */}
      {dateLines.map((line, i) => (
        <text
          key={`date-${i}`}
          x={46}
          y={dateBlockTop + i * DATE_LH + DATE_LH / 2}
          fontSize={11}
          fill="#64748b"
          dominantBaseline="middle"
        >
          {truncate(line, 22)}
        </text>
      ))}

      {/* Overlay-Status-Badge oben rechts */}
      {overlay && (
        <g transform={`translate(${NODE_W - 13},13)`}>
          <circle
            r={9}
            fill={overlay.hatched ? 'url(#overlay-unknown-hatch)' : overlay.badgeBg}
            stroke={overlay.ring}
            strokeWidth={1.5}
          />
          <text
            x={0}
            y={0.5}
            fontSize={overlay.symbol.length > 1 ? 9 : 11}
            fontWeight={700}
            fill={overlay.badgeFg}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {overlay.symbol}
          </text>
        </g>
      )}

      {/* ─── Erkundungsansicht: verdeckte Verwandte ──────────────────────
          Ein Knopf unten rechts mit der Zahl der Angehörigen, die gerade
          nicht auf der Fläche stehen. Er sitzt ÜBER dem Kartenrand und nicht
          daneben: daneben wanderte er beim Umbrechen der Namen mit, und man
          träfe ihn auf dem Handy nicht. */}
      {verdeckt != null && verdeckt > 0 && onAufdecken && (
        <g
          transform={`translate(${NODE_W - 15},${NODE_H - 13})`}
          onClick={(e) => {
            // Sonst wählte derselbe Klick zusätzlich die Person aus und
            // klappte das Detail-Panel auf.
            e.stopPropagation();
            onAufdecken(data.id);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onAufdecken(data.id);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`${verdeckt} weitere Angehörige von ${fullName} aufdecken`}
          style={{ cursor: 'pointer' }}
        >
          <title>
            {verdeckt === 1
              ? 'Ein weiterer Angehöriger – anzeigen'
              : `${verdeckt} weitere Angehörige – anzeigen`}
          </title>
          <circle r={11} fill="#1d4ed8" stroke="#ffffff" strokeWidth={1.5} />
          <text
            x={0}
            y={0.5}
            fontSize={verdeckt > 99 ? 8 : 10}
            fontWeight={700}
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {verdeckt > 99 ? '99+' : `+${verdeckt}`}
          </text>
        </g>
      )}

      {/* Hover-Effekt über CSS in <style> Tag */}
    </g>
  );
});
